import api from '@/lib/api'
import { ENDPOINTS } from '@/constants/endpoints'
import { StatutType } from '@/types/subscription'
import type { StripeInterval } from '@/constants/subscription'

export interface StripePrice {
  id: string
  currency: string
  unit_amount: number
  recurring: {
    interval: StripeInterval
    interval_count: number
    usage_type: string
  }
  type: string
  active: boolean
  discount_percent?: string | number
}

export interface StripeProduct {
  id: string
  name: string
  description: string | null
  active: boolean
  images: string[]
  metadata: {
    statut: StatutType
    type?: string
    licenses?: number | string
    licences?: number | string
    discount_percent?: string | number
  }
  prices: StripePrice[]
}

export interface StripeProductsResponse {
  count: number
  next: string | null
  previous: string | null
  results: StripeProduct[]
}

export interface CheckoutSessionResponse {
  url: string
}

export interface CheckoutSessionRequest {
  price_id: string
  customer_type: 'user' | 'organization'
  organization_id?: string | null
  success_url?: string
  cancel_url?: string
}

export type BillingPortalLocale = 'auto' | 'en' | 'fr'

export interface BillingPortalRequest {
  customer_type: 'user' | 'organization'
  organization_id?: string | null
  return_url: string
  locale?: BillingPortalLocale
}

export interface BillingPortalResponse {
  url: string
}

export interface ReactivationSessionRequest {
  success_url?: string
  cancel_url?: string
  locale?: BillingPortalLocale
}

export interface ReactivationSessionResponse {
  url: string
}

export const subscriptionService = {
  getSubscriptions: async (): Promise<StripeProductsResponse> => {
    const response = await api.get<StripeProductsResponse>(ENDPOINTS.STRIPE.PRODUCTS)
    return response.data
  },

  createCheckoutSession: async (
    params: CheckoutSessionRequest
  ): Promise<CheckoutSessionResponse> => {
    const body: Record<string, unknown> = {
      price_id: params.price_id,
      customer_type: params.customer_type,
    }
    if (params.customer_type === 'organization' && params.organization_id != null) {
      body.organization_id = params.organization_id
    }
    if (params.success_url != null) {
      body.success_url = params.success_url
    }
    if (params.cancel_url != null) {
      body.cancel_url = params.cancel_url
    }
    const response = await api.post<CheckoutSessionResponse>(ENDPOINTS.STRIPE.CHECKOUT, body)
    return response.data
  },

  createBillingPortalSession: async (
    params: BillingPortalRequest
  ): Promise<BillingPortalResponse> => {
    const body: Record<string, unknown> = {
      customer_type: params.customer_type,
      return_url: params.return_url,
      locale: params.locale ?? 'fr',
    }
    if (params.customer_type === 'organization') {
      body.organization_id = params.organization_id ?? null
    }
    const response = await api.post<BillingPortalResponse>(ENDPOINTS.STRIPE.BILLING_PORTAL, body)
    return response.data
  },

  createReactivationSession: async (
    params: ReactivationSessionRequest = {}
  ): Promise<ReactivationSessionResponse> => {
    const body: Record<string, unknown> = {
      locale: params.locale ?? 'fr',
    }
    if (params.success_url != null) {
      body.success_url = params.success_url
    }
    if (params.cancel_url != null) {
      body.cancel_url = params.cancel_url
    }
    const response = await api.post<ReactivationSessionResponse>(ENDPOINTS.STRIPE.REACTIVATE, body)
    return response.data
  },
}
